import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import qs from 'qs';

import {
  Button,
  Checkbox,
  Popover,
  Radio,
  Dropdown,
  Tooltip
} from '@salesforce/design-system-react';
import { SetupStep } from './';
import redirectToError from '../../utilities/redirectToError';
import { LANGUAGE_CODES } from '../constants';
import './Setup.scss';

export class Setup extends Component {
  constructor(props) {
    super(props);
    this.state = {
      popoverState: {
        showDeactivatePrompt: false
      },
      locationTypeState: {
        value: props.activation.locationType,
        applied: true
      },
      selectedLanguage: props.activation.settings.translationLanguage || 'en'
    };
  }

  handleUpdateLocationAndLanguage() {
    const parsedQueryString = qs.parse(this.props.location.search.substr(1));
    this.props.actions.submitLocationAndLanguageChange(
      {
        ...parsedQueryString,
        status: 'active',
        onError: redirectToError(this.props.history)
      },
      this.state.locationTypeState.value,
      this.state.selectedLanguage
    );
  }

  handleActivate(currentStatus) {
    const parsedQueryString = qs.parse(this.props.location.search.substr(1));
    const settings =
      this.props.activation.unsavedSettings &&
      this.props.activation.unsavedSettings !== this.props.activation.settings
        ? this.props.activation.unsavedSettings
        : this.props.activation.settings;

    // Translations must be disabled when English is selected
    const langBody =
      this.state.selectedLanguage === 'en'
        ? {
            translationEnabled: 'N',
            translationLanguage: ''
          }
        : {
            translationEnabled: 'Y',
            translationLanguage: this.state.selectedLanguage
          };

    this.props.actions.submitActivation(
      {
        ...parsedQueryString,
        status: 'active',
        onError: redirectToError(this.props.history)
      },
      {
        config: {
          maxTags: parseInt(settings.maxTags),
          minConfidence: parseFloat(settings.minConfidence),
          prefix: settings.prefix,
          ...langBody
        },
        locationType: this.state.locationTypeState.value
      },
      ['pause', 'deleted'].indexOf(currentStatus) !== -1 ? 'PUT' : 'POST'
    );
  }

  handleDeactivate() {
    const parsedQueryString = qs.parse(this.props.location.search.substr(1));
    this.props.actions.submitActivationStatusChange(
      {
        ...parsedQueryString,
        onError: redirectToError(this.props.history)
      },
      'pause'
    );
  }

  handleSaveSettings(settings) {
    this.props.actions.updateTagSettings(this.state.drawerState);
    this.props.actions.closeSettingsDrawer();
  }

  toggleDeactivatePrompt() {
    this.setState({
      popoverState: {
        showDeactivatePrompt: !this.state.popoverState.showDeactivatePrompt
      }
    });
  }

  transformLanguagesForDropdown(formatMessage) {
    return LANGUAGE_CODES.map(lang => {
      return {
        label: formatMessage({ id: `Languages.${lang}` }),
        value: lang
      };
    });
  }

  render() {
    const { formatMessage } = this.props.intl;
    const { settings, enabled, status, locationType } = this.props.activation;
    const isLanguageChanged =
      settings.translationLanguage === ''
        ? this.state.selectedLanguage !== 'en'
        : this.state.selectedLanguage !== settings.translationLanguage;

    return (
      <div className="setup">
        <SetupStep
          step={1}
          title={formatMessage({ id: 'SetupStep.1.title' })}
          description={
            <p>
              <FormattedMessage id="SetupStep.1.description" />
            </p>
          }
          rightNav={
            <Link
              to={{
                pathname: '/settings',
                search: this.props.location ? this.props.location.search : null
              }}
            >
              <Button variant="neutral">
                <FormattedMessage id="Setup.btn.settings" />
              </Button>
            </Link>
          }
          complete={settings.saved}
        />
        <SetupStep
          step={2}
          title={formatMessage({ id: 'SetupStep.2.title' })}
          description={
            <div>
              <p>
                <FormattedMessage id="SetupStep.2.description" />
              </p>
              <div className="location-setting-options">
                <div>
                  <Radio
                    id="all"
                    value="all"
                    checked={this.state.locationTypeState.value === 'all'}
                    onChange={(_, checked) =>
                      this.setState({
                        locationTypeState: { value: 'all', applied: false }
                      })
                    }
                    name="locationType"
                    labels={{
                      label: formatMessage({ id: 'LocationType.all.label' })
                    }}
                  />
                </div>
                <div>
                  <Radio
                    id="folder"
                    value="folder"
                    checked={this.state.locationTypeState.value === 'folder'}
                    onChange={(_, checked) =>
                      this.setState({
                        locationTypeState: { value: 'folder', applied: false }
                      })
                    }
                    name="locationType"
                    labels={{
                      label: formatMessage({ id: 'LocationType.folder.label' })
                    }}
                  />
                  <p className="radio-note">
                    {formatMessage({ id: 'LocationType.folder.note' })}
                  </p>
                </div>
              </div>
            </div>
          }
          rightNav={
            status !== 'inactive' ? (
              <Button
                variant="neutral"
                onClick={() => this.handleUpdateLocationAndLanguage()}
                disabled={
                  this.state.locationTypeState.value === locationType &&
                  !isLanguageChanged
                }
              >
                <FormattedMessage id="Setup.btn.update" />
              </Button>
            ) : null
          }
          complete={enabled}
        >
          <div className="slds-m-top_large slds-m-bottom_x-small language-header">
            <h2>{formatMessage({ id: 'SetupStep.2.subtitle' })}</h2>
            <Tooltip
              id="language-info"
              content={formatMessage({ id: 'SetupStep.2.info' })}
              triggerClassName="slds-m-left_xx-small"
              variant="learnMore"
            />
          </div>
          <Dropdown
            align="left"
            buttonClassName="language-dropdown"
            iconCategory="utility"
            iconName="down"
            iconPosition="right"
            label={formatMessage({
              id: `Languages.${this.state.selectedLanguage}`
            })}
            length={7}
            options={this.transformLanguagesForDropdown(formatMessage)}
            onSelect={lang =>
              this.setState({
                selectedLanguage: lang.value
              })
            }
            value={this.state.selectedLanguage}
            width="large"
          />
        </SetupStep>
        <SetupStep
          step={3}
          title={formatMessage({ id: 'SetupStep.3.title' })}
          description={
            <p>
              <FormattedMessage id="SetupStep.3.description" />
            </p>
          }
          rightNav={
            <div>
              <Popover
                isOpen={this.state.popoverState.showDeactivatePrompt}
                body={formatMessage({
                  id: 'ToggleActivate.DeactivatePrompt.body'
                })}
                footer={
                  <div className="slds-text-align_right">
                    <Button
                      label={formatMessage({
                        id: 'ToggleActivate.DeactivatePrompt.cancel'
                      })}
                      onClick={() => this.toggleDeactivatePrompt()}
                    />
                    <Button
                      label={formatMessage({
                        id: 'ToggleActivate.DeactivatePrompt.apply'
                      })}
                      variant="destructive"
                      onClick={() => this.handleDeactivate()}
                    />
                  </div>
                }
                heading={formatMessage({
                  id: 'ToggleActivate.DeactivatePrompt.heading'
                })}
                id="popover-controlled-with-footer"
                onRequestClose={() => this.toggleDeactivatePrompt()}
              >
                <Checkbox
                  id="activate-toggle"
                  variant="toggle"
                  labels={{
                    toggleDisabled: formatMessage({
                      id: 'ToggleActivate.label.off'
                    }),
                    toggleEnabled: formatMessage({
                      id: 'ToggleActivate.label.on'
                    })
                  }}
                  checked={enabled}
                  disabled={!settings.saved || status === 'toDelete'}
                  onChange={v => {
                    return v && !enabled && status !== 'active'
                      ? this.handleActivate(status)
                      : this.toggleDeactivatePrompt();
                  }}
                />
              </Popover>
            </div>
          }
          complete={enabled}
        />
      </div>
    );
  }
}

Setup.propTypes = {
  activation: PropTypes.object,
  children: PropTypes.element,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(Setup);
